/**
 * Settings › Help, second half: setting up, changing and fixing the system
 * itself — Cloudflare, PocketBase, Todoist, calendars, the Work Blocks script.
 *
 * The full reference is docs/SETUP.md in the repo, which also holds your own
 * server's details. These guides are the same steps, here because this is
 * where you look; keep the two in step when either changes. If the app itself
 * won't load, docs/SETUP.md is the one to use.
 *
 * The Work Blocks script is bundled from docs/work-blocks/WorkBlocks.gs, so
 * "Copy the script" always hands over the version this build expects.
 */

import { useState } from "react";

import workBlocksScript from "../../../docs/work-blocks/WorkBlocks.gs?raw";

import { Guide, listStyle, subList } from "./guideParts";

const CF = <>Cloudflare → Workers &amp; Pages → <b>project-hub</b> → Settings → <b>Variables and Secrets</b></>;

export function SystemGuides() {
  return (
    <>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)", margin: "18px 0 2px" }}>Setting up the system</div>
      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>
        Cloudflare, PocketBase, Todoist, your calendars and the Work Blocks script. The full reference, with your
        server's details, is <code>docs/SETUP.md</code> in the project-hub repo; use it if the app itself won't load.
      </div>

      <Guide title="How the pieces fit together">
        <ul style={listStyle}>
          <li><b>PocketBase</b>, on your own server, holds all the data and logins. The Workers reach it through a <b>Cloudflare Tunnel</b>.</li>
          <li><b>The Worker "project-hub"</b> on Cloudflare serves this app and makes every outside call: PocketBase, Todoist, your calendars, the Work Blocks script. Pushing to GitHub deploys it.</li>
          <li><b>Todoist</b> is where projects, sections and tasks are created. Project Hub mirrors it and never writes back.</li>
          <li><b>Your Google calendars</b> are read for the Today grid, meeting suggestions and free time.</li>
          <li><b>The Apps Script "Work Blocks"</b> (script.google.com) reads your busy time and writes work blocks, only when Project Hub asks.</li>
          <li><b>Reimburse</b>, the phone expense app, has its own Worker and shares the same PocketBase.</li>
        </ul>
        <p style={{ margin: "8px 0 0" }}>
          Worker settings live in {CF}, never in the "Build" section, which the running Worker can't see. After adding or
          changing one, click <b>Deploy</b>, then reload this page with Cmd+Shift+R.
        </p>
      </Guide>

      <Guide title="Setting up from scratch">
        <ol style={listStyle}>
          <li><b>PocketBase running and reachable through the tunnel.</b> Your server's steps are in <code>docs/SETUP.md</code>.</li>
          <li>
            <b>Import the schema.</b> PocketBase admin → Settings → Import collections, with <b>"Delete missing collections
            and fields" unchecked</b>. On a new database import <code>pocketbase/pb_schema.json</code>; otherwise just the files
            you're missing:
            <ul style={subList}>
              <li><code>pb_schema_expenses.json</code>: Expenses and Reimburse</li>
              <li><code>pb_schema_planner.json</code>: Planner › Revenue &amp; capacity</li>
              <li><code>pb_schema_invoices.json</code>: the invoice builder</li>
              <li><code>pb_schema_workblocks.json</code>: Planner › Week ahead</li>
            </ul>
            Then create your login in PocketBase's <code>users</code> collection.
          </li>
          <li><b>Deploy the Worker.</b> Connect the GitHub repo in Cloudflare (project-hub → Settings → Builds). From then on, every push deploys.</li>
          <li>
            <b>Turn it on.</b> In {CF} add:
            <ul style={subList}>
              <li><code>APP_MODE</code> = <code>live</code></li>
              <li><code>POCKETBASE_URL</code> = your PocketBase address through the tunnel</li>
              <li><code>CF_ACCESS_CLIENT_ID</code> and <code>CF_ACCESS_CLIENT_SECRET</code>, only if Cloudflare Access guards the tunnel</li>
            </ul>
          </li>
          <li><b>Todoist.</b> Add the secret <code>TODOIST_TOKEN</code> (from Todoist → Settings → Integrations → Developer). Then Settings › Connections → <b>Test connection</b> → <b>Sync now</b>.</li>
          <li><b>Your calendars</b> (optional). Settings › Calendars → add each calendar's private address. Google: Calendar settings → the calendar → Integrate calendar → <i>Secret address in iCal format</i>.</li>
          <li><b>Deadline feed</b> (optional). Settings › Calendars → "How do I set up the calendar subscription?"</li>
          <li><b>Work blocks</b> (optional). The next guide.</li>
          <li><b>Reimburse</b> (optional). Its Worker needs only <code>POCKETBASE_URL</code>. Sign in with the same login.</li>
        </ol>
      </Guide>

      <Guide title="Connecting work blocks to Google Calendar">
        <ol style={listStyle}>
          <li><b>Import</b> <code>pocketbase/pb_schema_workblocks.json</code> into PocketBase (as above).</li>
          <li>
            <b>Make a token</b> and keep this page open: you'll paste it in two places.
            <TokenMaker />
          </li>
          <li><b>Add it to the Worker.</b> In {CF} → + Add → Type <i>Secret</i>, name <code>BLOCKS_TOKEN</code>, value the token → Deploy.</li>
          <li><b>Turn off the old script</b>, if you used the Work Blocks spreadsheet: open it → Extensions → Apps Script → ⏰ Triggers → on the <code>planWeek</code> row, ⋮ → Delete trigger.</li>
          <li>
            <b>Create the new script.</b> Go to script.google.com → New project, and name it <code>Work Blocks</code>.
            Copy the script, then in the editor press Cmd+A, Cmd+V, Cmd+S.
            <CopyScript />
          </li>
          <li>
            <b>Script settings.</b> ⚙ Project Settings:
            <ul style={subList}>
              <li>Time zone: Los Angeles.</li>
              <li>Script Properties → Add script property: <code>BLOCKS_TOKEN</code> = the same token → Save script properties.</li>
            </ul>
          </li>
          <li><b>Test it.</b> Back in the Editor, choose <code>testSetup</code> → Run. Approve access when asked (Review permissions → your account → Advanced → Go to Work Blocks → Allow). The log should say "BLOCKS_TOKEN is set".</li>
          <li>
            <b>Publish it for Project Hub.</b> Deploy → New deployment → ⚙ → <b>Web app</b>, Execute as <b>Me</b>, Who has access
            <b> Anyone</b> → Deploy. Copy the Web app URL (ends in <code>/exec</code>).
          </li>
          <li><b>Add that URL to the Worker.</b> In {CF} → + Add → <i>Secret</i>, name <code>BLOCKS_SCRIPT_URL</code> → Deploy.</li>
          <li><b>Check it.</b> Settings › Connections → Work blocks → <b>Test connection</b>. It reads next week's calendars without writing anything.</li>
        </ol>
        <p style={{ margin: "8px 0 0" }}>
          "Anyone" sounds open, but the script answers only requests carrying your token, and even then it will only touch
          this week and the next two, at most 60 blocks at a time, and never your own events.
        </p>
      </Guide>

      <Guide title="Changing passwords, tokens or the script">
        <ul style={listStyle}>
          <li><b>Your Project Hub password:</b> if the deadline feed is set up, also update <code>FEED_PASSWORD</code> on the Worker. Work blocks don't use it.</li>
          <li><b><code>BLOCKS_TOKEN</code>:</b> make a new one (above) and change it in both places, the Worker and the script's Script Properties, so they match.</li>
          <li><b><code>ICS_TOKEN</code>:</b> change it on the Worker. Every calendar subscription to the deadline feed stops; subscribe again with the new link.</li>
          <li><b><code>TODOIST_TOKEN</code>:</b> regenerate it in Todoist, then update it on the Worker.</li>
          <li>
            <b>The Work Blocks script</b> (new code, or a change to which calendars count as busy): edit and save, then Deploy →
            Manage deployments → ✎ → Version: <b>New version</b> → Deploy. Saving alone doesn't change what Project Hub
            talks to. The URL stays the same.
          </li>
          <li><b>Turn work blocks off:</b> untick "Plan the week automatically" in Planner › Week ahead › Standing blocks › Hours &amp; defaults. To cut the connection entirely: Apps Script → Deploy → Manage deployments → Archive.</li>
          <li><b>A new feature with a schema file:</b> import it into PocketBase before pushing the code.</li>
        </ul>
      </Guide>

      <Guide title="When something isn't working">
        <p style={{ margin: "8px 0 0" }}>Start at Settings › Connections: Todoist and work blocks each say whether they're connected and what's wrong.</p>
        <ul style={listStyle}>
          <li><b>You see the demo, not your data:</b> <code>APP_MODE</code> isn't <code>live</code> on the Worker.</li>
          <li><b>Nothing loads / "Couldn't reach the server":</b> PocketBase or the tunnel is down. See <code>docs/SETUP.md</code> for restarting them.</li>
          <li><b>A setting you added seems ignored:</b> it went under "Build" variables, or the name has a typo or a stray space. Move it to Variables and Secrets, Deploy, reload with Cmd+Shift+R.</li>
          <li><b>Todoist "No token" or "rejected the token":</b> <code>TODOIST_TOKEN</code> is missing or was revoked.</li>
          <li><b>New Todoist tasks don't appear:</b> the mirror syncs at most every 5 minutes. Settings › Connections → Sync now.</li>
          <li><b>A calendar shows an error:</b> its private address was reset in Google. Copy it again into Settings › Calendars.</li>
          <li><b>"Not connected to Google yet":</b> <code>BLOCKS_TOKEN</code> or <code>BLOCKS_SCRIPT_URL</code> is missing on the Worker.</li>
          <li><b>"The Apps Script rejected the token":</b> the token differs between the Worker and the script.</li>
          <li><b>"The Apps Script answered with a web page":</b> the deployment isn't set to Anyone, or the URL is the <code>/dev</code> one.</li>
          <li><b>A block says "No open slot":</b> its window or days were too tight that week. Widen them.</li>
          <li><b>Blocks at odd hours:</b> the script's time zone (⚙ Project Settings) isn't Los Angeles.</li>
          <li><b>No automatic planning:</b> it's off, the week was already sent, or the app hasn't been opened since the planning time.</li>
        </ul>
      </Guide>
    </>
  );
}

/** A 48-character random token, made in this browser and never stored. */
function TokenMaker() {
  const [token, setToken] = useState("");
  const [copied, setCopied] = useState(false);
  function make() {
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    setToken([...bytes].map((b) => b.toString(16).padStart(2, "0")).join(""));
    setCopied(false);
  }
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "6px 0 2px" }}>
      <button type="button" onClick={make} style={button}>{token ? "Make another" : "Make a token"}</button>
      {token && (
        <>
          <code style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, background: "var(--tint)", color: "var(--ink)", wordBreak: "break-all" }}>{token}</code>
          <button type="button" style={button} onClick={() => void navigator.clipboard.writeText(token).then(() => setCopied(true))}>
            {copied ? "Copied" : "Copy"}
          </button>
        </>
      )}
      <span style={{ width: "100%", fontSize: 11.5, color: "var(--muted2)" }}>Made here and never saved. Treat it like a password.</span>
    </div>
  );
}

/** The bundled Work Blocks script, onto the clipboard. */
function CopyScript() {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "6px 0 2px" }}>
      <button
        type="button"
        style={button}
        onClick={() => void navigator.clipboard.writeText(workBlocksScript).then(() => setState("copied"), () => setState("failed"))}
      >
        {state === "copied" ? "Copied the script" : "Copy the script"}
      </button>
      <span style={{ fontSize: 11.5, color: state === "failed" ? "var(--bad)" : "var(--muted2)" }}>
        {state === "failed"
          ? "The browser blocked copying. Use docs/work-blocks/WorkBlocks.gs from the repo instead."
          : `${workBlocksScript.split("\n").length} lines, the version this build of Project Hub expects.`}
      </span>
    </div>
  );
}

const button: React.CSSProperties = {
  padding: "6px 12px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", fontSize: 12.5, color: "var(--ink2)", cursor: "pointer",
};
